import React, { type ReactNode, useEffect, useState } from 'react';
import {
  Box,
  Button,
  FooterActionBar,
  H2,
  InlineMessage,
  Label,
  MenuInput,
  type MenuInputOption,
} from '@saviynt/design-system';

import {
  type InvertedLocalize,
  type LocalizationMessages,
  useInvertedLocalize,
} from '../../../shared/utils/localization';
import {
  getGeneralConfiguration,
  saveGeneralConfiguration,
} from '../data-sources';

import './SSOSettings.css';

/** Global SSO settings */
export function SSOSettings(): ReactNode {
  const l = useInvertedLocalize();
  const [isPending, setIsPending] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [hasSaveError, setHasSaveError] = useState(false);
  const [usernameMappingSelectedOptions, setUsernameMappingSelectedOptions] =
    useState<MenuInputOption[]>([]);
  const [usernameMappingSelectedValues, setUsernameMappingSelectedValues] =
    useState<string[]>([]);
  const [multiIdpLookupSelectedValue, setMultiIdpLookupSelectedValue] =
    useState<string | null>(null);
  const isSaveDisabled =
    isPending || usernameMappingSelectedValues.length === 0;
  const multiIdpLookupOptions = [...generateCustomProperties(l)].map((o) => ({
    ...o,
    isChecked: o.value === multiIdpLookupSelectedValue,
  }));
  const usernameOptions = [
    { label: l('Email'), value: 'email' },
    { label: l('Username'), value: 'username' },
    { label: l('System Username'), value: 'systemUserName' },
    ...generateCustomProperties(l),
  ];

  const handleUsernameMappingChange = (
    _: MenuInputOption,
    selections: MenuInputOption[] = []
  ) => {
    setUsernameMappingSelectedValues(selections.map(({ value }) => value));
  };

  const handleMultiIdpLookupChange = (o?: MenuInputOption) => {
    setMultiIdpLookupSelectedValue(o?.value ?? null);
  };

  const handleSaveClick = () => {
    setIsPending(true);
    setHasSaveError(false);
    saveGeneralConfiguration(
      usernameMappingSelectedValues,
      multiIdpLookupSelectedValue
    )
      .then(() => {
        setIsSaved(true);
        setTimeout(() => {
          setIsSaved(false);
        }, 5000);
      })
      .catch(() => {
        setHasSaveError(true);
      })
      .finally(() => setIsPending(false));
  };

  useEffect(() => {
    getGeneralConfiguration()
      .then((settings) => {
        setIsPending(false);
        setMultiIdpLookupSelectedValue(settings.multiIdpLookup);
        setUsernameMappingSelectedValues(settings.usernameMapping);
        setUsernameMappingSelectedOptions(
          usernameOptions
            .filter((o) => settings.usernameMapping.includes(o.value))
            .map((o) => ({ ...o, isChecked: true }))
        );
      })
      .catch(() => {
        setHasLoadError(true);
        setIsPending(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only want to run on mount
  }, []);

  return (
    <>
      <Box className='Auth_SSOSettings'>
        <H2>{l('Basic Configurations')}</H2>
        {hasLoadError && (
          <InlineMessage
            text={l(
              'An error occurred while loading your configurations. Please refresh this page to try again.'
            )}
            colorTheme='critical'
          />
        )}
        <MenuInput
          className='Auth_SSOSettings_multiIdpLookup'
          label='Auth_SSOSettings_multiIdpLookup'
          onChange={handleMultiIdpLookupChange}
          options={multiIdpLookupOptions}
          isDisabled={isPending}
          isResetButtonVisible={multiIdpLookupSelectedValue !== null}
          onInputReset={() => setMultiIdpLookupSelectedValue(null)}
          inputLabel={
            <Label htmlFor='Auth_SSOSettings_multiIdpLookup'>
              {l('Multi-IdP Lookup')}
            </Label>
          }
        />
        <MenuInput
          className='Auth_SSOSettings_usernameMapping'
          label='Auth_SSOSettings_usernameMapping'
          isMulti
          isDisabled={isPending}
          onChange={handleUsernameMappingChange}
          options={usernameOptions}
          selectedOptions={usernameMappingSelectedOptions}
          setSelectedOptions={setUsernameMappingSelectedOptions}
          inputLabel={
            <Label htmlFor='Auth_SSOSettings_usernameMapping'>
              {l('Saviynt Username')}
            </Label>
          }
        />
        {hasSaveError && (
          <InlineMessage
            text={l(
              'An error occurred while saving your configurations. Please try again.'
            )}
            colorTheme='critical'
          />
        )}
      </Box>
      <FooterActionBar>
        <Button
          className='Auth_SSOSettings_saveButton'
          isDisabled={isSaveDisabled}
          onClick={handleSaveClick}
          isSuccess={isSaved}
          successText={l('Saved!')}>
          {l('Save')}
        </Button>
      </FooterActionBar>
    </>
  );
}

function* generateCustomProperties(l: InvertedLocalize) {
  // Note: should use `CustomProperty${i}.Default.label` for translations
  for (let i = 1; i <= 65; i += 1) {
    yield {
      label: l(`Custom Property ${i}` as LocalizationMessages),
      value: `customproperty${i}`,
    };
  }
}
